import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { m as motion, AnimatePresence } from 'framer-motion';
import { css, cx, etiColors } from '@eti/styles';
import { DangerousHtmlContent, variants, ContentWrapper, Notification } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { getSeatMapSegmentText } from '../utils/getSeatMapSegmentText';
import { getBestPriceCharacteristics, getSegment } from '../../utils/segments';
import { SEATING_PREFERENCE_OPTIONS } from '../../utils/constants';
import SeatMapDeck from '../../components/SeatMapDeck';
import SeatMapTravelers from '../../components/SeatMapTravelers';
import DeckSelection from './DeckSelection';
import SeatMapLegend from './legend/SeatMapLegend';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const mainContentStyles = css`
  background-color: ${etiColors.white};
  border-radius: 0 0 0.1875rem 0.1875rem;
  border-top: 1px solid ${etiColors.grayScale[6]};
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: 100%;
`;
const selectionWrapperStyles = css`
  background-color: ${etiColors.white};
  box-shadow: 0 0 5px ${etiColors.grayScale[8]};
  position: relative;
`;
const seatPersuasionWrapperStyles = css`
  background-color: ${etiColors.white};
  box-shadow: 0 2px 2px ${etiColors.grayScale[6]};
  position: relative;
`;
const seatMapDeckWrapperStyles = css`
  background: ${etiColors.grayScale[3]};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
`;
const motionVariants = {
  enter: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: '-100%'
  }
};
const seatNotificationWrapperStyles = css`
  position: absolute;
  width: 100%;
  z-index: 3;
`;
const seatNotificationStyles = css`
  border: none;
  padding: 0.5rem;

  .etiHtmlContent {
    font-size: 0.625rem;
  }

  button {
    right: 0.6rem;
    top: 0.6rem;
  }

  @media (min-width: 48em) {
    padding: 1rem;
    .etiHtmlContent {
      font-size: 0.875rem;
    }
    button {
      right: 1rem;
      top: 1rem;
    }
  }
`;
const getInitialSegmentTextVisibility = seatMapSegmentTexts => seatMapSegmentTexts.reduce((acc, item) => ({
  ...acc,
  [`${item.boundIndex}-${item.segmentIndex}`]: Boolean(item.text)
}), {});
const SeatMapMainContent = _ref => {
  let {
    active = [],
    shouldAnimate,
    isFromSummary,
    segments,
    direction,
    deckOptions = [],
    onChangeSeat,
    onChangeTraveler,
    onDeckChange,
    onRemoveSeat,
    productSpecificTexts: {
      seatMapSegmentTexts = []
    } = {},
    selectedDeck,
    selectedSeats,
    travelers,
    onNextTravelerClick,
    onPreviousTravelerClick,
    handleBackDrop
  } = _ref;
  const {
    t
  } = useTranslation();
  const [activeSegment] = active;
  const text = getSeatMapSegmentText(activeSegment, seatMapSegmentTexts);
  const initialSegmentTextVisibility = getInitialSegmentTextVisibility(seatMapSegmentTexts);
  const [segmentTextVisibility, setSegmentTextVisibility] = useState(initialSegmentTextVisibility);
  const bestPriceCharacteristics = getBestPriceCharacteristics(segments, activeSegment);
  const currentSegment = getSegment(segments, activeSegment);
  const isSeatingPreferenceAvailable = currentSegment && currentSegment.__typename && (currentSegment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE || currentSegment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE);
  const handleNotificationClose = () => {
    setSegmentTextVisibility({
      ...segmentTextVisibility,
      ...{
        [activeSegment]: false
      }
    });
  };
  useEffect(() => {
    let notificationCloseTimeOut;
    if (segmentTextVisibility[activeSegment]) {
      notificationCloseTimeOut = setTimeout(() => {
        setSegmentTextVisibility({
          ...segmentTextVisibility,
          ...{
            [activeSegment]: false
          }
        });
      }, 15000);
    }
    return () => {
      clearTimeout(notificationCloseTimeOut);
    };
  }, [segmentTextVisibility, activeSegment]);
  return /*#__PURE__*/_jsxs("div", {
    className: cx(mainContentStyles, 'seatMap__main', shouldAnimate ? 'animate' : ''),
    "data-testid": "seatmap-mainContent",
    children: [!isSeatingPreferenceAvailable && currentSegment.isAvailable && /*#__PURE__*/_jsx("div", {
      className: selectionWrapperStyles,
      children: /*#__PURE__*/_jsx(SeatMapTravelers, {
        active: active,
        onChange: onChangeTraveler,
        onNextTravelerClick: onNextTravelerClick,
        onPreviousTravelerClick: onPreviousTravelerClick,
        onRemoveSeat: onRemoveSeat,
        selectedSeats: selectedSeats,
        travelers: travelers
      })
    }), /*#__PURE__*/_jsx("div", {
      className: seatPersuasionWrapperStyles,
      children: /*#__PURE__*/_jsx(AnimatePresence, {
        children: text && segmentTextVisibility[activeSegment] && /*#__PURE__*/_jsx(motion.div, {
          animate: "enter",
          className: seatNotificationWrapperStyles,
          exit: "exit",
          transition: {
            type: 'tween',
            duration: 0.3,
            delay: 0.3
          },
          variants: motionVariants,
          children: /*#__PURE__*/_jsx(ContentWrapper, {
            children: /*#__PURE__*/_jsx(Notification, {
              className: seatNotificationStyles,
              closeButtonDataTestId: "seatMap-close-Notification",
              closeButtonLabel: t('Close'),
              dataTestId: "seatMap-seatingPersuasion-Notification",
              onClose: handleNotificationClose,
              variant: variants.INFORMATION,
              children: /*#__PURE__*/_jsx(DangerousHtmlContent, {
                content: text
              })
            })
          })
        }, "seatmap-notification")
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: seatMapDeckWrapperStyles,
      children: [deckOptions.length > 1 && /*#__PURE__*/_jsx(DeckSelection, {
        onValueChange: onDeckChange,
        options: deckOptions,
        selected: selectedDeck
      }), /*#__PURE__*/_jsx(SeatMapDeck, {
        active: active,
        direction: direction,
        isFromSummary: isFromSummary,
        onChange: onChangeSeat,
        onDeckChange: onDeckChange,
        onNextTravelerClick: onNextTravelerClick,
        segments: segments,
        selectedDeck: selectedDeck,
        selectedSeats: selectedSeats,
        travelers: travelers
      })]
    }), bestPriceCharacteristics && /*#__PURE__*/_jsx(SeatMapLegend, {
      bestPriceCharacteristics: bestPriceCharacteristics,
      handleBackDrop: handleBackDrop
    })]
  });
};
SeatMapMainContent.propTypes = {
  active: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  deckOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  })),
  direction: PropTypes.oneOf([-1, 0, 1]),
  handleBackDrop: PropTypes.func,
  isFromSummary: PropTypes.bool,
  onChangeSeat: PropTypes.func,
  onChangeTraveler: PropTypes.func,
  onDeckChange: PropTypes.func,
  onNextTravelerClick: PropTypes.func,
  onPreviousTravelerClick: PropTypes.func,
  onRemoveSeat: PropTypes.func,
  productSpecificTexts: PropTypes.shape({
    seatMapSegmentTexts: PropTypes.arrayOf(PropTypes.shape({
      boundIndex: PropTypes.number,
      segmentIndex: PropTypes.number,
      text: PropTypes.string
    }))
  }),
  segments: PropTypes.arrayOf(PropTypes.shape({
    from: PropTypes.shape({
      airport: PropTypes.string,
      city: PropTypes.string
    }),
    to: PropTypes.shape({
      airport: PropTypes.string,
      city: PropTypes.string
    })
  })),
  selectedDeck: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  selectedSeats: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.any), PropTypes.arrayOf(PropTypes.any)]),
  shouldAnimate: PropTypes.bool,
  travelers: PropTypes.arrayOf(PropTypes.shape({
    ageType: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }))
};
export default SeatMapMainContent;
//# sourceMappingURL=SeatMapMainContent.js.map